import type { Tile, Wind } from '../rules/tiles';
import type { PatternHit, PlayerId } from '../rules/types';
import type { Game, RoundRecord } from '../game/types';
import type { GameSummary } from '../game/summary';
import { formatSettlementExplanation } from './settlementExplain';

/**
 * Heavenly Hand has no real "winning tile" — the dealer's whole starting
 * hand is already complete before any tile exchange happens, so there's no
 * particular tile to point at as the one that "won" it. Scoring doesn't
 * care which tile `HandInput.winningTile` holds here (Heavenly Hand is a
 * forced limit hand, checked before any tile-identity-dependent pattern
 * logic runs), so if nothing was tapped and the Heavenly Hand box is
 * ticked, fall back to any tile already in the hand rather than blocking
 * submission on a selection that carries no real meaning for this pattern.
 */
export function resolveWinningTile(
  picked: Tile | undefined,
  concealed: Tile[],
  heavenlyHand: boolean,
): Tile | undefined {
  if (picked) return picked;
  if (heavenlyHand && concealed.length > 0) return concealed[0];
  return undefined;
}

export function currentTotals(game: Game): Record<PlayerId, number> {
  const totals: Record<PlayerId, number> = {};
  for (const p of game.players) totals[p.id] = 0;
  for (const round of game.rounds) {
    for (const payment of round.payments) {
      totals[payment.player] = (totals[payment.player] ?? 0) + payment.delta;
    }
  }
  return totals;
}

export function playerName(game: Game, id: PlayerId | undefined): string {
  if (!id) return '?';
  return game.players.find((p) => p.id === id)?.name ?? id;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * "Sun, 6 September 2026 01.00 PM" — the Final Results screen's game
 * start/end timestamps, on both the main device and the guest's mirrored
 * view. A custom format (not a locale preset) so it reads identically
 * regardless of the device's own locale settings.
 */
export function formatGameTimestamp(ms: number): string {
  const d = new Date(ms);
  const day = DAY_NAMES[d.getDay()]!;
  const date = d.getDate();
  const month = MONTH_NAMES[d.getMonth()]!;
  const year = d.getFullYear();
  let hours = d.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const hoursStr = String(hours).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}, ${date} ${month} ${year} ${hoursStr}.${minutes} ${ampm}`;
}

/**
 * What a corrected round originally was, as plain text — shown on screen (escaped) and in the
 * PDF, so the two can never disagree. Undefined for a round that was never edited.
 * `original` reads e.g. "originally Theo won (off Yani), 4 fan: Theo +8, Yani -8."
 */
export function editedNoteParts(game: Game, r: RoundRecord): { times: string; original: string } | undefined {
  if (!r.edited) return undefined;
  const o = r.edited.original;
  const moves = o.payments
    .filter((p) => p.delta !== 0)
    .map((p) => `${playerName(game, p.player)} ${formatDelta(p.delta)}`)
    .join(', ');
  const times = r.edited.count > 1 ? ` (corrected ${r.edited.count} times)` : '';

  if (r.outcome === 'draw') {
    const tenpai = (o.tenpai ?? []).map((id) => playerName(game, id)).join(', ') || 'nobody';
    return { times, original: `originally ${tenpai} tenpai${moves ? `: ${moves}` : ''}.` };
  }
  const who = playerName(game, o.winner ?? r.winner);
  const source = o.selfDraw ? 'self-draw' : `off ${playerName(game, o.discarder)}`;
  const fan = `${o.effectiveFan ?? 0} fan${o.isLimit ? ' (limit)' : ''}`;
  return { times, original: `originally ${who} won (${source}), ${fan}${moves ? `: ${moves}` : ''}.` };
}

/** The same note as one line of text (used by the PDF): "Edited — originally Theo won ...". */
export function editedNoteText(game: Game, r: RoundRecord): string | undefined {
  const parts = editedNoteParts(game, r);
  return parts ? `Edited${parts.times} — ${parts.original}` : undefined;
}

/**
 * With an exponential payout base and a high fan cap, a single hand's points
 * can run into the thousands or tens of thousands (e.g. 2^15 = 32,768) —
 * comma-grouped so a big number stays readable at a glance instead of
 * needing to be counted digit by digit.
 */
export function formatDelta(delta: number): string {
  const formatted = delta.toLocaleString('en-US');
  return delta > 0 ? `+${formatted}` : formatted;
}

/**
 * formatDelta's text, colored by sign, for HTML display contexts (the
 * scoreboard, round history, end-of-game summary). Not used for the
 * transient post-submit message text, which is escaped as a whole string
 * elsewhere and would render these tags literally.
 */
export function deltaSpan(delta: number): string {
  const cls = delta > 0 ? 'delta-positive' : delta < 0 ? 'delta-negative' : '';
  const text = formatDelta(delta);
  return cls ? `<span class="${cls}">${text}</span>` : text;
}

const WIND_LABEL: Record<Wind, string> = {
  east: 'East',
  south: 'South',
  west: 'West',
  north: 'North',
};

export function windLabel(w: Wind): string {
  return WIND_LABEL[w];
}

/** Escapes text for safe interpolation into innerHTML template strings. */
export function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Substitutes every `{winner}` in an admin-authored banner template with the
 * (also escaped) winner name(s) — used for the Final Results winner banner.
 * Escapes the template itself first, since it's free text an admin typed
 * into a form and this ends up in innerHTML; `{`/`}` survive escaping
 * untouched, so the placeholder still matches after that pass.
 */
export function formatWinnerBannerText(template: string, winnerNames: string): string {
  return esc(template).replaceAll('{winner}', esc(winnerNames));
}

/**
 * Plain-text (no markup) Final Results recap for the native share sheet —
 * short enough to land readably inside a WhatsApp/Telegram message bubble,
 * unlike the full PDF. Ranked one line per player, using the same
 * `formatDelta` sign convention as everywhere else in this app.
 */
export function buildResultsShareText(game: Game, summary: GameSummary): string {
  const winnerNames = summary.winners.map((id) => playerName(game, id)).join(', ');
  const lines = summary.players.map((p) => `${p.rank}. ${p.name} ${formatDelta(p.totalScore)}`);
  const label = summary.winners.length > 1 ? 'Tied' : 'Winner';
  return [
    `Mahjong Results — Table ${game.tableId}`,
    `${label}: ${winnerNames} · ${summary.roundsPlayed} round${summary.roundsPlayed === 1 ? '' : 's'} played`,
    '',
    ...lines,
  ].join('\n');
}

/**
 * A short buzz confirming a round (or a guest's submission) actually went
 * through — useful in a noisy game-centre room where someone might not be
 * staring at the screen waiting for the on-screen confirmation. Silently
 * does nothing on a device/browser without vibration support (desktop,
 * iOS Safari) rather than throwing.
 */
export function vibrateSuccess(): void {
  navigator.vibrate?.(60);
}

/**
 * Which patterns contributed to a round's fan, as a row of small chips — each
 * one tappable to explain what it means (see patternGlossary.ts, wired up
 * once at startup via a delegated listener, so this stays a plain string
 * builder with no dependency on it).
 */
export function formatPatternBreakdown(patterns: PatternHit[]): string {
  if (patterns.length === 0) return '';
  const chips = patterns
    .map(
      (p) =>
        `<button type="button" class="tag pattern-chip" data-pattern-key="${esc(p.key)}" data-pattern-label="${esc(p.label)}">${esc(p.label)} +${p.fan}</button>`,
    )
    .join('');
  return `<div class="breakdown">${chips}</div>`;
}

/**
 * The photo(s) used for a round's tile recognition, as tap-to-enlarge
 * thumbnails. Tapping is handled by a delegated listener (see lightbox.ts)
 * rather than a link, deliberately — these are `data:` URLs, and navigating
 * a new tab to one crashed the browser on at least one real device.
 */
export function formatPhotoThumbnails(photos: string[] | undefined): string {
  if (!photos?.length) return '';
  const imgs = photos
    .map((src) => `<img src="${src}" alt="Recognition photo" class="round-photo" />`)
    .join('');
  return `<div class="round-photos">${imgs}</div>`;
}

function handTileImg(tile: string, extraClass = ''): string {
  return `<img class="hand-tile ${extraClass}" src="/tile-icons/${tile}.png" alt="${esc(tile)}" />`;
}

/**
 * Pictures the winning hand from the tile-icon set. Always rendered
 * alongside any camera photo, not just as a fallback for manually-entered
 * rounds — a real photo can be cropped, blurry, or ambiguous about which
 * tile actually won, so this gives a guaranteed-legible reference to check
 * it against, especially in print/PDF output. Skipped only when the round
 * predates `concealed` being stored.
 *
 * Concealed tiles and any declared kong sit in one merged rack — kongs
 * aren't broken out onto their own line. Flowers/seasons stay on their own
 * line since they're a different kind of thing (bonus tiles, not part of
 * the 14-tile hand shape), not because kongs need distinguishing.
 *
 * Deliberately in `concealed`'s stored order (tap order for manual entry,
 * photo reading order for a scan), not sorted by suit/rank — the operator
 * entered or scanned it in a specific order for a reason, and re-sorting
 * for print would break the same tile-by-tile correspondence the on-screen
 * entry/review flow is careful to preserve.
 */
export function formatHandTiles(round: RoundRecord): string {
  if (!round.concealed?.length) return '';

  const allTiles = [...round.concealed, ...(round.melds ?? []).flatMap((m) => m.tiles)];
  const rack = allTiles
    .map((t) => handTileImg(t, t === round.winningTile ? 'is-winning' : ''))
    .join('');

  const bonus = (round.flowers ?? [])
    .map((t) => handTileImg(t))
    .join('');

  return `
    <div class="hand-tiles">
      <div class="hand-rack">${rack}</div>
      ${bonus ? `<div class="hand-rack hand-bonus">${bonus}</div>` : ''}
    </div>
  `;
}

/**
 * Short plain-text summary of a round ("Yani won off Oma" / "Yani won
 * (self-draw)" / "Draw"), no markup — for contexts like a delete
 * confirmation where the full `<li>` breakdown would be too much.
 */
export function formatRoundOneLine(game: Game, r: RoundRecord): string {
  if (r.outcome === 'draw') return 'Draw';
  const winner = playerName(game, r.winner);
  const source = r.selfDraw ? '(self-draw)' : `off ${playerName(game, r.discarder)}`;
  return `${winner} won ${source}`;
}

/**
 * One round's `<li>` in both the in-game round history and the end-of-game
 * summary/PDF — identical in both places, so it lives here once instead of
 * two copies drifting apart. The point deltas sit right in the headline
 * (same bold line as the winner and fan) rather than as a separate, muted
 * line below the hand — the win and its payout are one fact, not two.
 */
/** The "Edited" line under a corrected round's headline — empty for a round that was never corrected. */
function formatEditedNote(game: Game, r: RoundRecord): string {
  const parts = editedNoteParts(game, r);
  return parts
    ? `<div class="round-edited"><span class="edited-tag">Edited${esc(parts.times)}</span> ${esc(parts.original)}</div>`
    : '';
}

/** "1st", "2nd", "3rd", "4th" ... for a ranking. */
export function ordinal(n: number): string {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/** `extraHtml` is placed at the end of the item (the host's play screen puts the "Edit this round" button there). */
export function formatRoundListItem(game: Game, r: RoundRecord, extraHtml = ''): string {
  if (r.outcome === 'draw') {
    return `
      <li>
        <span class="headline">#${r.index} — Draw</span>
        ${formatEditedNote(game, r)}
        ${formatSettlementExplanation(game, r)}
        ${extraHtml}
      </li>
    `;
  }

  const winner = esc(playerName(game, r.winner));
  const source = r.selfDraw ? 'self-draw' : `off ${esc(playerName(game, r.discarder))}`;
  const deltas = r.payments
    .filter((p) => p.delta !== 0)
    .map((p) => `${esc(playerName(game, p.player))} ${deltaSpan(p.delta)}`)
    .join(', ');

  return `
    <li>
      <div class="headline">#${r.index} — ${winner} won (${source}), ${r.effectiveFan ?? 0} fan${r.isLimit ? ' (limit)' : ''} — ${deltas}</div>
      ${formatEditedNote(game, r)}
      ${formatPatternBreakdown(r.patterns ?? [])}
      ${formatSettlementExplanation(game, r)}
      ${formatPhotoThumbnails(r.photos)}
      ${formatHandTiles(r)}
      ${extraHtml}
    </li>
  `;
}

/**
 * "Select All" for a list where only some rows are showing (a search is
 * active): selects exactly the showing rows, or — when they are all already
 * selected — clears the selection. It never includes a row that isn't shown,
 * so "Select All" followed by "Delete" can only remove what the person can see.
 */
export function toggleSelectAll(visibleIds: string[], selected: ReadonlySet<string>): Set<string> {
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  return allSelected ? new Set() : new Set(visibleIds);
}

/**
 * Past Games search: a game matches when its search text contains EVERY word
 * of the query, in any order and case ("theo budi" finds a game with both
 * players; "theo table 3" narrows further). An empty query matches everything.
 * `blob` is the per-game searchable text.
 */
export function matchesSearch(blob: string, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const text = blob.toLowerCase();
  return words.every((w) => text.includes(w));
}
